import { ENTITLEMENT_FEATURES, ENTITLEMENT_LIMITS, FEATURE_LABELS, LIMIT_LABELS, type PackageConfig } from '@lp/contracts';
import { Check, Minus } from 'lucide-react';
import { num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { SettingsList } from './settings';
import { cn } from './ui';

/** a limit as the owner reads it: a number, or "No limit" */
export function LimitValue({ limit }: { limit: number | null }) {
  const t = useT();
  return <>{limit == null ? t('No limit') : num(limit)}</>;
}

/** the products a package allows: every product (including ones added later), or these */
export function ProductsAllowed({ products }: { products: 'all' | string[] }) {
  const t = useT();
  if (products === 'all') return <span>{t('Every product, including new ones')}</span>;
  if (!products.length) return <span className="text-ink-500">{t('None')}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {products.map((p) => (
        <span key={p} className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[11px] text-ink-700">
          {p}
        </span>
      ))}
    </span>
  );
}

/** a feature switched on or off */
export function FeatureLine({ on, label }: { on: boolean; label: string }) {
  return (
    <li className={cn('flex items-center gap-2 py-1 text-sm', on ? 'text-ink-900' : 'text-ink-400')}>
      {on ? <Check size={14} className="text-emerald-600" /> : <Minus size={14} />}
      {label}
    </li>
  );
}

/** what one package version gives: products, limits, features — read only */
export function PackageContent({ config }: { config: PackageConfig }) {
  const t = useT();
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Products')}</h3>
        <ProductsAllowed products={config.products} />
      </div>
      <div>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Limits')}</h3>
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
          {ENTITLEMENT_LIMITS.map((k) => (
            <div key={k} className="contents">
              <dt className="text-ink-600">{t(LIMIT_LABELS[k])}</dt>
              <dd className="text-right tabular-nums font-medium">
                <LimitValue limit={config.limits[k]} />
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Features')}</h3>
        <ul>
          {ENTITLEMENT_FEATURES.map((f) => (
            <FeatureLine key={f} on={config.features[f]} label={t(FEATURE_LABELS[f])} />
          ))}
        </ul>
      </div>
      <div className="md:col-span-3">
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Settings')}</h3>
        <SettingsList map={config.settings} />
      </div>
    </div>
  );
}

/** "5 of 7" features on */
export function featuresOn(config: PackageConfig): number {
  return ENTITLEMENT_FEATURES.filter((f) => config.features[f]).length;
}
